import React from 'react';
import styles from '../NeedInfoV2.module.scss';
import { formatDateByFormat, DATE_FORMATS } from '../../../utils/dateUtils';

export interface CompletedStatusCellProps {
    status: 'PENDING' | 'COMPLETE';
    markedAt: string | null;
    markedByName?: string;
    createdAt: string;
    formatDate: (dateString: string | null) => string;
    isPno: boolean;
    /** Non-interactive guard — mirrors the row's existing mark-complete disabled conditions. */
    disabled: boolean;
    /** Toggles the requirement's status (PENDING ↔ COMPLETE). Fired from either state's circle. */
    onToggle: () => void;
}

/**
 * Renders the entire status cell for a Data Pending row (RECUR-27563 Section B design fix):
 * a single clickable circle + one status label + a timestamp — replacing the previous
 * "Mark Complete" radio + separate "Pending"/"Completed" meta stack.
 *
 * PENDING: hollow circle outlined in --status-pending-red + "Pending" label (red) + createdAt.
 *   Clicking the circle (when enabled) calls onToggle to mark the requirement COMPLETE.
 * COMPLETE: filled green check circle + label ("by {markedByName}" for a PNO viewer when
 *   resolved (AC6), else "Completed") + timestamp from markedAt (or createdAt when markedAt
 *   is absent — system-completed / unresolved, AC7). Clicking the circle (when enabled) calls
 *   onToggle to re-open the requirement back to PENDING (RECUR-27563 regression fix — the
 *   redesign had made COMPLETE one-way; the previous radio control was two-way).
 */
const CompletedStatusCell: React.FC<CompletedStatusCellProps> = ({
    status,
    markedAt,
    markedByName,
    createdAt,
    formatDate,
    isPno,
    disabled,
    onToggle,
}) => {
    const isComplete = status === 'COMPLETE';

    const handleToggle = () => {
        if (disabled) return;
        onToggle();
    };

    if (isComplete) {
        const showMarkedByName = !!(markedAt && isPno && markedByName);
        const label = showMarkedByName ? `by ${markedByName}` : 'Completed';
        const timestamp = markedAt
            ? showMarkedByName
                ? formatDateByFormat(markedAt, DATE_FORMATS.TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY)
                : formatDate(markedAt)
            : formatDate(createdAt);

        return (
            <div className={styles.StatusMeta}>
                <div className={styles.StatusLabelRow}>
                    <span
                        className={styles.CompleteCircle}
                        style={disabled ? { cursor: 'not-allowed', opacity: 0.6 } : { cursor: 'pointer' }}
                        role="button"
                        aria-label="Mark requirement pending"
                        tabIndex={disabled ? -1 : 0}
                        onClick={handleToggle}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleToggle();
                            }
                        }}
                    />
                    <span className={styles.CompleteLabel}>{label}</span>
                </div>
                <div>{timestamp}</div>
            </div>
        );
    }

    return (
        <div className={styles.StatusMeta}>
            <div className={styles.StatusLabelRow}>
                <span
                    className={styles.PendingCircle}
                    style={disabled ? { cursor: 'not-allowed', opacity: 0.6 } : undefined}
                    role="button"
                    aria-label="Mark requirement complete"
                    tabIndex={disabled ? -1 : 0}
                    onClick={handleToggle}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleToggle();
                        }
                    }}
                />
                <span className={styles.PendingLabel}>Pending</span>
            </div>
            <div>{formatDate(createdAt)}</div>
        </div>
    );
};

export default CompletedStatusCell;
